"use client";

/**
 * generation-context.tsx
 *
 * Global React context managing ONE background report generation at a time.
 *
 * Two entry points:
 * 1. "I'll read it later" — iframe spawned immediately
 * 2. "Navigate away mid-watch" — generate page signals on unmount → iframe spawned
 *
 * Enforces:
 * - Only ONE generation at a time (client-side guard)
 * - Minimize/expand (X = hide, not cancel)
 * - Auto-reappear on complete when minimized
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";

export type GenerationStatus = "generating" | "complete" | "failed" | null;

export interface GenerationState {
  status: GenerationStatus;
  reportType: "stock" | "fund";
  reportName: string;
  generatePageUrl: string;
  reportUrl: string | null;
  progress: number;
  totalSections: number;
  minimized: boolean;          // X = minimize, not cancel
  /** Section content streamed from the iframe — keyed by section name */
  sections: Record<string, unknown>;
  /** Report metadata (header info) from the iframe */
  metadata: Record<string, unknown> | null;
  /** Framework score (stock only) */
  frameworkScore: Record<string, unknown> | null;
  /** News items (stock only) */
  newsItems: unknown[];
}

interface GenerationContextType {
  generation: GenerationState | null;
  /** Returns true if a generation is currently active (generating via iframe) */
  isGenerating: boolean;
  /** Start background generation via iframe. Returns false if one is already running. */
  startBackgroundGeneration: (opts: {
    reportType: "stock" | "fund";
    reportName: string;
    generatePageUrl: string;
  }) => boolean;
  /** Called by generate page on unmount if not done */
  onGeneratePageUnmount: (opts: {
    reportType: "stock" | "fund";
    reportName: string;
    generatePageUrl: string;
  }) => void;
  /** Called by generate page on mount */
  onGeneratePageMount: () => void;
  /** Minimize the mini-player (keep generating) */
  minimize: () => void;
  /** Expand from minimized dot */
  expand: () => void;
  /** Fully dismiss (only when complete or failed) */
  dismiss: () => void;
}

const GenerationContext = createContext<GenerationContextType>({
  generation: null,
  isGenerating: false,
  startBackgroundGeneration: () => false,
  onGeneratePageUnmount: () => {},
  onGeneratePageMount: () => {},
  minimize: () => {},
  expand: () => {},
  dismiss: () => {},
});

export function useGeneration() {
  return useContext(GenerationContext);
}

export function GenerationProvider({ children }: { children: React.ReactNode }) {
  const [generation, setGeneration] = useState<GenerationState | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const watchingRef = useRef(false);

  const isGenerating =
    generation?.status === "generating" || iframeUrl !== null;

  const startBackgroundGeneration = useCallback(
    (opts: {
      reportType: "stock" | "fund";
      reportName: string;
      generatePageUrl: string;
    }): boolean => {
      // Block if already generating
      if (iframeUrl) return false;

      const separator = opts.generatePageUrl.includes("?") ? "&" : "?";
      const url = `${opts.generatePageUrl}${separator}mode=iframe`;

      setGeneration({
        status: "generating",
        reportType: opts.reportType,
        reportName: opts.reportName,
        generatePageUrl: opts.generatePageUrl,
        reportUrl: null,
        progress: 0,
        totalSections: opts.reportType === "fund" ? 8 : 9,
        minimized: false,
        sections: {},
        metadata: null,
        frameworkScore: null,
        newsItems: [],
      });
      setIframeUrl(url);
      watchingRef.current = false;
      return true;
    },
    [iframeUrl]
  );

  const onGeneratePageMount = useCallback(() => {
    watchingRef.current = true;
  }, []);

  const onGeneratePageUnmount = useCallback(
    (opts: {
      reportType: "stock" | "fund";
      reportName: string;
      generatePageUrl: string;
    }) => {
      watchingRef.current = false;
      // Only spawn iframe if no background generation is already running
      if (!iframeUrl) {
        const separator = opts.generatePageUrl.includes("?") ? "&" : "?";
        const url = `${opts.generatePageUrl}${separator}mode=iframe`;

        setGeneration({
          status: "generating",
          reportType: opts.reportType,
          reportName: opts.reportName,
          generatePageUrl: opts.generatePageUrl,
          reportUrl: null,
          progress: 0,
          totalSections: opts.reportType === "fund" ? 8 : 9,
          minimized: false,
          sections: {},
          metadata: null,
          frameworkScore: null,
          newsItems: [],
        });
        setIframeUrl(url);
      }
    },
    [iframeUrl]
  );

  const minimize = useCallback(() => {
    setGeneration((prev) => (prev ? { ...prev, minimized: true } : prev));
  }, []);

  const expand = useCallback(() => {
    setGeneration((prev) => (prev ? { ...prev, minimized: false } : prev));
  }, []);

  const dismiss = useCallback(() => {
    setGeneration(null);
    setIframeUrl(null);
  }, []);

  // Listen for postMessage from the iframe
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (!event.data || typeof event.data !== "object") return;
      const { type } = event.data;

      if (type === "generation-progress") {
        const count = event.data.sectionsCompleted ?? 0;
        const sectionKey = event.data.sectionKey as string | undefined;
        const sectionData = event.data.sectionData as unknown | undefined;
        const isFrameworkScore = event.data.isFrameworkScore as boolean | undefined;
        setGeneration((prev) => {
          if (!prev) return prev;
          const updated = {
            ...prev,
            progress: Math.min(count, prev.totalSections),
          };
          if (sectionKey && sectionData) {
            if (isFrameworkScore) {
              updated.frameworkScore = sectionData as Record<string, unknown>;
            } else {
              updated.sections = { ...prev.sections, [sectionKey]: sectionData };
            }
          }
          return updated;
        });
      } else if (type === "generation-metadata") {
        setGeneration((prev) =>
          prev ? { ...prev, metadata: event.data.metadata } : prev
        );
      } else if (type === "generation-news") {
        setGeneration((prev) =>
          prev ? { ...prev, newsItems: event.data.news ?? [] } : prev
        );
      } else if (type === "generation-complete") {
        const reportUrl =
          event.data.reportType === "fund"
            ? `/funds/${event.data.slug}`
            : `/report/${event.data.slug ?? event.data.reportId}`;

        setGeneration((prev) =>
          prev
            ? {
                ...prev,
                status: "complete",
                reportUrl,
                progress: prev.totalSections,
                // Auto-reappear when complete (even if minimized)
                minimized: false,
              }
            : prev
        );
        setIframeUrl(null);
      } else if (type === "generation-failed") {
        setGeneration((prev) =>
          prev
            ? {
                ...prev,
                status: "failed",
                // Auto-reappear when failed (even if minimized)
                minimized: false,
              }
            : prev
        );
        setIframeUrl(null);
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return (
    <GenerationContext.Provider
      value={{
        generation,
        isGenerating,
        startBackgroundGeneration,
        onGeneratePageUnmount,
        onGeneratePageMount,
        minimize,
        expand,
        dismiss,
      }}
    >
      {children}

      {/* Hidden iframe — keeps SSE connection alive */}
      {iframeUrl && (
        <iframe
          ref={iframeRef}
          src={iframeUrl}
          style={{
            position: "fixed",
            width: 0,
            height: 0,
            border: "none",
            opacity: 0,
            pointerEvents: "none",
          }}
          aria-hidden
          tabIndex={-1}
        />
      )}
    </GenerationContext.Provider>
  );
}
